import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ArrowUpDown } from 'lucide-react';
import { brl, pct, num, days } from './format';

const METRICS = {
  pct_frete: { label: '% do frete sobre o pedido', fmt: pct },
  frete_medio: { label: 'Frete médio', fmt: brl },
  prazo_medio: { label: 'Prazo médio prometido', fmt: null },
  pedidos: { label: 'Número de pedidos', fmt: num },
};
const COLS = [
  ['uf', 'UF'], ['pedidos', 'Pedidos'], ['frete_medio', 'Frete médio'], ['pct_frete', '% frete'],
  ['prazo_medio', 'Prazo'], ['pct_gratis', '% grátis'],
];

let geoCache = null;
const useGeo = () => {
  const [geo, setGeo] = useState(geoCache);
  useEffect(() => {
    if (geoCache) return;
    fetch('/geo/br-states.json').then((r) => r.json()).then((g) => { geoCache = g; setGeo(g); }).catch(() => {});
  }, []);
  return geo;
};

const UfTable = ({ rows, unidade }) => {
  const [sort, setSort] = useState({ key: 'pedidos', dir: -1 });
  const sorted = useMemo(() => [...rows].sort((a, b) => {
    const va = a[sort.key] ?? -Infinity;
    const vb = b[sort.key] ?? -Infinity;
    return (va > vb ? 1 : va < vb ? -1 : 0) * sort.dir;
  }), [rows, sort]);
  const fmt = { pedidos: num, frete_medio: brl, pct_frete: pct, prazo_medio: (v) => days(v, unidade), pct_gratis: pct };
  return (
    <div className="overflow-x-auto max-h-[460px] overflow-y-auto" data-testid="logistics-uf-table">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-card">
          <tr className="border-b">
            {COLS.map(([k, l]) => (
              <th key={k} className="py-2 px-2 text-left font-medium text-muted-foreground whitespace-nowrap">
                <button type="button" className="inline-flex items-center gap-1 hover:text-foreground" data-testid={`uf-sort-${k}`}
                  onClick={() => setSort((s) => ({ key: k, dir: s.key === k ? -s.dir : -1 }))}>
                  {l} <ArrowUpDown className="h-3 w-3" />
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => (
            <tr key={r.uf} className="border-b last:border-0" data-testid={`uf-row-${r.uf}`}>
              <td className="py-2 px-2 font-medium">{r.uf}</td>
              {COLS.slice(1).map(([k]) => <td key={k} className="py-2 px-2 font-mono whitespace-nowrap">{fmt[k](r[k])}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const BrazilMap = ({ rows, unidade }) => {
  const geo = useGeo();
  const [metric, setMetric] = useState('frete_medio');
  const [hover, setHover] = useState(null);
  const byUf = useMemo(() => Object.fromEntries(rows.map((r) => [r.uf, r])), [rows]);
  const max = Math.max(...rows.map((r) => r[metric] || 0), 0);
  const fmt = METRICS[metric].fmt || ((v) => days(v, unidade));
  const color = (uf) => {
    const v = byUf[uf]?.[metric];
    if (v == null) return '#e2e8f0';
    if (!max) return 'rgba(91, 91, 214, 0.15)';
    return `rgba(91, 91, 214, ${0.15 + 0.85 * (v / max)})`;
  };

  return (
    <div className="grid gap-4 lg:grid-cols-5">
      <Card className="hidden md:block lg:col-span-3" data-testid="logistics-map-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="text-sm text-muted-foreground">Colorir por</span>
            <Select value={metric} onValueChange={setMetric}>
              <SelectTrigger className="w-64" data-testid="logistics-map-metric"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(METRICS).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="relative" onMouseLeave={() => setHover(null)}>
            {geo ? (
              <svg viewBox={`0 0 ${geo.width} ${geo.height}`} className="w-full h-auto max-h-[460px]" data-testid="logistics-map">
                {geo.states.map((s) => (
                  <path key={s.uf} d={s.d} fill={color(s.uf)} stroke="#ffffff" strokeWidth={3} strokeLinejoin="round"
                    className="cursor-pointer transition-opacity hover:opacity-80" data-testid={`map-uf-${s.uf}`}
                    onMouseMove={(e) => {
                      const box = e.currentTarget.ownerSVGElement.getBoundingClientRect();
                      setHover({ uf: s.uf, name: s.name, x: e.clientX - box.left, y: e.clientY - box.top });
                    }} />
                ))}
              </svg>
            ) : <div className="h-[400px] rounded bg-muted animate-pulse" />}
            {hover && (
              <div className="absolute pointer-events-none z-10 rounded-md border bg-popover px-3 py-2 text-xs shadow-md"
                style={{ left: hover.x + 12, top: hover.y + 12 }} data-testid="logistics-map-tooltip">
                <p className="font-medium mb-1">{hover.name} ({hover.uf})</p>
                {byUf[hover.uf] ? (
                  <>
                    <p>Pedidos: {num(byUf[hover.uf].pedidos)}</p>
                    <p>Frete médio: {brl(byUf[hover.uf].frete_medio)}</p>
                    <p>Frete sobre o pedido: {pct(byUf[hover.uf].pct_frete)}</p>
                    <p>Prazo médio: {days(byUf[hover.uf].prazo_medio, unidade)}</p>
                  </>
                ) : <p className="text-muted-foreground">Sem pedidos no período</p>}
              </div>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">Escala: {fmt(0)} → {fmt(max)}</p>
        </CardContent>
      </Card>
      <Card className="min-w-0 lg:col-span-2">
        <CardContent className="p-4"><UfTable rows={rows} unidade={unidade} /></CardContent>
      </Card>
    </div>
  );
};
